import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "../constants/queryKeys";
import enMessages from "../../messages/en.json";
import jaMessages from "../../messages/ja.json";

type Messages = Record<string, any>;

const LOCAL_MESSAGES: Record<string, Messages> = {
  en: enMessages as Messages,
  ja: jaMessages as Messages,
};

const FETCH_TIMEOUT_MS = 8000;

function getLocalMessages(locale: string): Messages {
  return LOCAL_MESSAGES[locale] ?? LOCAL_MESSAGES.en;
}

/** Deep merge: base first, then overrides (remote). Keeps nested structure for next-intl. */
function mergeMessages(base: Messages, overrides: Messages): Messages {
  const result = { ...base };
  for (const key of Object.keys(overrides)) {
    const baseVal = result[key];
    const overrideVal = overrides[key];
    if (
      overrideVal != null &&
      typeof overrideVal === "object" &&
      !Array.isArray(overrideVal) &&
      baseVal != null &&
      typeof baseVal === "object" &&
      !Array.isArray(baseVal)
    ) {
      result[key] = mergeMessages(baseVal as Messages, overrideVal as Messages);
    } else {
      result[key] = overrideVal;
    }
  }
  return result;
}

/**
 * Fetch messages from remote config, merged with local. Local is the base;
 * remote config overrides. On fetch failure, returns local only.
 */
async function fetchMessagesFromRemoteConfig(
  locale: string
): Promise<Messages> {
  const local = getLocalMessages(locale);
  const baseUrl = import.meta.env.VITE_AVACUS_LOCALIZATION_BASE_URL;
  if (!baseUrl) {
    return local;
  }

  const url = `${baseUrl}/localization_${locale}.json`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!res.ok) {
      console.warn(
        `Remote config returned ${res.status} for locale ${locale}, using local messages`
      );
      return local;
    }
    const data = await res.json();
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return mergeMessages(local, data);
    }
    console.warn(
      `Invalid remote config response format for locale ${locale}, using local messages`
    );
    return local;
  } catch (error) {
    clearTimeout(timeoutId);
    const isTimeout = error instanceof Error && error.name === "AbortError";
    console.warn(
      isTimeout
        ? `Translation fetch timed out for locale ${locale}, using local messages`
        : `Error fetching remote translation messages for locale ${locale}, using local messages:`,
      isTimeout ? undefined : error
    );
    return local;
  }
}

export const useTranslationMessages = ({
  locale,
  enabled = true,
}: {
  locale: string;
  enabled?: boolean;
}): any => {
  return useQuery({
    queryKey: [QUERY_KEY.translationMessages, locale],
    queryFn: async () => await fetchMessagesFromRemoteConfig(locale),
    // Local messages can render immediately without marking remote data fresh.
    placeholderData: enabled ? getLocalMessages(locale) : undefined,
    enabled: enabled && !!locale,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};
